<template>
    <div v-if="visible" class="dialog-backdrop">
        <transition name="zoom">
            <div v-if="!isLoading" class="building-card">
                <div class="card-header">
                    <div class="left-section">
                        <h2>Building Overview</h2>
                        <div v-if="showCard" class="info-card">
                            <button class="close-btn" @click="closeCard">×</button>
                            <span class="card-title">
                                <span style="font-weight: bold; color: gold;">{{ getFloorName(activeFloor) }}</span>
                                <hr style="border: none; border-top: 1px dashed #ccc; margin: 10px 0;" />
                                <p>Total Rooms: <span style="color: chartreuse;">{{ getTotalRooms(activeFloor) }}</span>
                                </p>
                                <p v-for="category in getFloorCategories(activeFloor)" :key="category.id">
                                    {{ category.name }}: <span style="color: chartreuse;">{{ category.pois.length
                                        }}</span>
                                </p>
                            </span>
                        </div>
                    </div>
                    <div class="right-section">
                        <div class="zoom-controls">
                            <button @click="zoomIn" :class="{ 'selected': isZoomInActive }">＋</button>
                            <button @click="zoomOut" :class="{ 'selected': isZoomOutActive }">－</button>
                            <button @click="resetZoom" :class="{ 'selected': isResetActive }"
                                class="reset-btn">Reset</button>
                            <button @click="closeDialog" class="dialog-close-btn">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                                    xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 6L18 18M6 18L18 6" stroke="white" stroke-width="2"
                                        stroke-linecap="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="building-container">
                    <object id="svgObject" type="image/svg+xml" :data="svgPath" @load="onSvgLoad"
                        class="scaled-svg"></object>
                </div>
            </div>
        </transition>
        <div v-if="isLoading" class="loader-container">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, defineExpose } from 'vue';
import svgPanZoom from 'svg-pan-zoom';

const svgPath = ref('/assets/hbc.svg');
const activeFloor = ref(null);
const floorIds = ref(['floor1', 'floor2', 'floor3', 'floor4']);
const svgScale = ref(1);
const isZoomInActive = ref(false);
const isZoomOutActive = ref(false);
const isResetActive = ref(false);
const showCard = ref(false);
const isLoading = ref(false);
const visible = ref(false);
let panZoomInstance = null;

const dummyFloors = [
    {
        number: '1',
        name: 'Floor 1',
        poiCategories: [
            { id: 1, name: 'Meeting Rooms', pois: Array(5).fill({}) },
            { id: 2, name: 'Offices', pois: Array(7).fill({}) },
        ],
    },
    {
        number: '2',
        name: 'Floor 2',
        poiCategories: [
            { id: 3, name: 'Meeting Rooms', pois: Array(3).fill({}) },
            { id: 4, name: 'Offices', pois: Array(9).fill({}) },
        ],
    },
    {
        number: '3',
        name: 'Floor 3',
        poiCategories: [
            { id: 5, name: 'Meeting Rooms', pois: Array(6).fill({}) },
            { id: 6, name: 'Offices', pois: Array(6).fill({}) },
        ],
    },
    {
        number: '4',
        name: 'Floor 4',
        poiCategories: [
            { id: 7, name: 'Meeting Rooms', pois: Array(4).fill({}) },
            { id: 8, name: 'Offices', pois: Array(8).fill({}) },
        ],
    },
];

const API_FLOORS = ref(dummyFloors);

const getFloorName = (floor) => {
    const selected = floor?.replace(/\D/g, '');
    const found = API_FLOORS.value.find(f => f.number === selected);
    return found?.name || '';
};

const getFloorCategories = (floor) => {
    const selected = floor?.replace(/\D/g, '');
    const found = API_FLOORS.value.find(f => f.number === selected);
    return found?.poiCategories || [];
};

const getTotalRooms = (floor) => {
    return getFloorCategories(floor).reduce((total, cat) => total + cat.pois.length, 0);
};

const openBuilding = () => {
    visible.value = true;
    isLoading.value = true;
    setTimeout(() => {
        isLoading.value = false;
        resetSelection(); // Reset selection when dialog opens
    }, 500);
};

const closeBuilding = () => {
    visible.value = false;
    isLoading.value = false;
    resetSelection();
};

const closeDialog = () => closeBuilding();

const closeCard = () => {
    resetSelection();
};

const zoomIn = () => {
    if (panZoomInstance) {
        panZoomInstance.zoomIn();
        isZoomInActive.value = true;
        setTimeout(() => (isZoomInActive.value = false), 300);
    }
};

const zoomOut = () => {
    if (panZoomInstance) {
        panZoomInstance.zoomOut();
        isZoomOutActive.value = true;
        setTimeout(() => (isZoomOutActive.value = false), 300);
    }
};

const resetZoom = () => {
    if (panZoomInstance) {
        panZoomInstance.resetZoom();
        isResetActive.value = true;
        setTimeout(() => (isResetActive.value = false), 300);
    }
};

const calculateSvgScale = (svgObject, svgElement) => {
    const svgRect = svgObject.getBoundingClientRect();
    const viewBoxStr = svgElement.getAttribute('viewBox');
    if (!viewBoxStr) return (svgScale.value = 1);
    const viewBox = viewBoxStr.split(' ').map(Number);
    if (!viewBox[2] || !viewBox[3]) return (svgScale.value = 1);
    const scaleX = svgRect.width / viewBox[2];
    const scaleY = svgRect.height / viewBox[3];
    svgScale.value = Math.max(Math.min(scaleX, scaleY), 0.5);
};

const onSvgLoad = () => {
    setTimeout(() => {
        const svgObject = document.getElementById('svgObject');
        const svgDoc = svgObject?.contentDocument;
        const svgElement = svgDoc?.querySelector('svg');
        if (!svgDoc || !svgElement) {
            console.error('SVG failed to load or is missing <svg> element');
            return;
        }

        if (!svgElement.getAttribute('viewBox')) {
            svgElement.setAttribute('viewBox', '0 0 5000 5000');
        }

        panZoomInstance = svgPanZoom(svgElement, {
            zoomEnabled: true,
            panEnabled: true,
            controlIconsEnabled: false,
            mouseWheelZoomEnabled: true,
            dblClickZoomEnabled: true,
            minZoom: 0.3,
            maxZoom: 10,
            fit: true,
            center: true,
        });

        calculateSvgScale(svgObject, svgElement);

        // Log available elements to debug
        console.log('SVG Document Elements:', svgDoc.querySelectorAll('*'));

        floorIds.value.forEach((id) => {
            const area = svgDoc.getElementById(`${id}_area`);
            if (area) {
                area.style.display = 'none';
                console.log(`Found ${id}_area:`, area);
            } else {
                console.warn(`Could not find ${id}_area in SVG`);
            }
        });

        svgElement.addEventListener('click', (event) => {
            const target = event.target;
            const isFloorButton = floorIds.value.some((id) => {
                const btn = svgDoc.getElementById(`${id}_btn`);
                return btn && (target === btn || btn.contains(target));
            });

            if (!isFloorButton) {
                resetSelection();
            }
        });

        floorIds.value.forEach((id) => {
            const ellipse = svgDoc.getElementById(`${id}_btn`);
            if (!ellipse || ellipse.tagName.toLowerCase() !== 'ellipse') {
                console.warn(`Could not find ellipse with ID ${id}_btn or it is not an ellipse`);
                return;
            }

            const cx = parseFloat(ellipse.getAttribute('cx'));
            const cy = parseFloat(ellipse.getAttribute('cy'));
            const parentGroup = ellipse.closest('g');
            if (!parentGroup || isNaN(cx) || isNaN(cy)) {
                console.warn(`Invalid parent group or coordinates for ${id}_btn: cx=${cx}, cy=${cy}`);
                return;
            }

            console.log(`Processing ${id}_btn: cx=${cx}, cy=${cy}`);

            const matrix = parentGroup.getAttribute('transform')?.match(/matrix\(([^)]+)\)/);
            let localX = cx;
            let localY = cy;
            if (matrix) {
                const [scaleX, , , scaleY, tx, ty] = matrix[1].split(',').map(Number);
                localX = (cx - tx) / scaleX;
                localY = (cy - ty) / scaleY;
                console.log(`Transformed coordinates for ${id}_btn: localX=${localX}, localY=${localY}`);
            }

            parentGroup.removeChild(ellipse);

            const svgBtn = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svgBtn.setAttribute('id', `${id}_btn`);
            svgBtn.setAttribute('x', localX + 120);
            svgBtn.setAttribute('y', localY + 80);
            svgBtn.setAttribute('width', 150);
            svgBtn.setAttribute('height', 50);
            svgBtn.style.cursor = 'pointer';
            svgBtn.style.touchAction = 'manipulation';

            const rect = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'rect');
            rect.setAttribute('x', 0);
            rect.setAttribute('y', 0);
            rect.setAttribute('width', 150);
            rect.setAttribute('height', 50);
            rect.setAttribute('rx', 10);
            rect.setAttribute('fill', '#4a2a2a');
            rect.style.transition = 'all 0.3s ease';

            const text = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'text');
            text.setAttribute('x', 75);
            text.setAttribute('y', 32);
            text.setAttribute('font-family', 'Calibri');
            text.setAttribute('font-size', '30');
            text.setAttribute('font-weight', 'bold');
            text.setAttribute('fill', '#fff');
            text.setAttribute('text-anchor', 'middle');
            text.textContent = id.replace('floor', 'Floor ');

            svgBtn.appendChild(rect);
            svgBtn.appendChild(text);

            svgBtn.addEventListener('click', () => highlightFloor(id));
            svgBtn.addEventListener('touchstart', (e) => {
                e.preventDefault();
                highlightFloor(id);
            });

            svgBtn.addEventListener('mouseover', () => {
                if (activeFloor.value !== id) {
                    rect.setAttribute('fill', '#6b3c3c');
                    rect.style.filter = 'drop-shadow(0 0 5px rgba(255, 100, 100, 0.5))';
                }
            });
            svgBtn.addEventListener('mouseout', () => {
                rect.style.filter = 'none';
                rect.setAttribute('fill', activeFloor.value === id ? '#90ee90' : '#4a2a2a');
            });

            parentGroup.appendChild(svgBtn);
            console.log(`Added SVG button for ${id}:`, svgBtn);
        });

        resetSelection(); // Ensure initial state is reset
    }, 500);
};

const highlightFloor = (id) => {
    const svgDoc = document.getElementById('svgObject')?.contentDocument;
    if (!svgDoc) return;

    floorIds.value.forEach((floorId) => {
        const area = svgDoc.getElementById(`${floorId}_area`);
        if (area) {
            area.style.display = floorId === id ? 'inline' : 'none';
        }

        const btn = svgDoc.getElementById(`${floorId}_btn`);
        if (btn) {
            const rect = btn.querySelector('rect');
            const text = btn.querySelector('text');
            if (rect) {
                rect.setAttribute('fill', floorId === id ? '#90ee90' : '#4a2a2a');
                rect.style.filter = floorId === id ? 'drop-shadow(0 0 5px rgba(144, 238, 144, 0.5))' : 'none';
            }
            if (text) {
                text.setAttribute('fill', floorId === id ? '#222' : '#fff');
            }
        }
    });

    activeFloor.value = id;
    showCard.value = true;
};

const resetSelection = () => {
    const svgDoc = document.getElementById('svgObject')?.contentDocument;
    if (!svgDoc) return;

    showCard.value = false;
    activeFloor.value = null;

    floorIds.value.forEach((floorId) => {
        const area = svgDoc.getElementById(`${floorId}_area`);
        if (area) {
            area.style.display = 'none';
        }

        const btn = svgDoc.getElementById(`${floorId}_btn`);
        if (btn) {
            const rect = btn.querySelector('rect');
            const text = btn.querySelector('text');
            if (rect) {
                rect.setAttribute('fill', '#4a2a2a');
                rect.style.filter = 'none';
            }
            if (text) {
                text.setAttribute('fill', '#fff');
            }
        }
    });
};

const onResize = () => {
    const svgObject = document.getElementById('svgObject');
    const svgDoc = svgObject?.contentDocument;
    const svgElement = svgDoc?.querySelector('svg');
    if (!svgElement) return;

    if (panZoomInstance) {
        panZoomInstance.resize();
        panZoomInstance.fit();
        panZoomInstance.center();
    }
};

onMounted(() => {
    window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize);
    if (panZoomInstance) {
        panZoomInstance.destroy();
        panZoomInstance = null;
    }
});
defineExpose({ openBuilding });
</script>

<style scoped>
.dialog-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.building-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
    padding: 16px;
    width: 90vw;
    max-width: 1200px;
    height: 90vh;
    max-height: 800px;
    display: flex;
    flex-direction: column;
    position: relative;
}

.loader-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
}

.zoom-enter-active,
.zoom-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.zoom-enter-from,
.zoom-leave-to {
    transform: scale(0.7);
    opacity: 0;
}

.zoom-enter-to,
.zoom-leave-from {
    transform: scale(1);
    opacity: 1;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
}

.left-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
}

.right-section {
    display: flex;
    margin-top: 0.5rem;
    align-items: flex-start;
}

.card-header h2 {
    color: #2c3e50;
    margin: 0 0 10px 0;
    font-size: 24px;
}

.info-card {
    background: #2c3e50;
    border-radius: 8px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: fit-content;
    max-width: 100%;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
    margin-bottom: 10px;
    position: absolute;
    z-index: 10;
    top: 40px;
    left: 0;
}

.card-title {
    color: #fff;
    font-size: 14px;
    font-weight: bold;
    text-align: left;
}

.close-btn {
    background: none;
    border: none;
    color: #fff;
    font-size: 18px;
    cursor: pointer;
    padding: 0 5px;
    line-height: 1;
    position: absolute;
    top: 5px;
    right: 5px;
}

.close-btn:hover {
    color: #ff5555;
}

.zoom-controls {
    display: flex;
    gap: 10px;
    align-items: center;
}

.zoom-controls button,
.zoom-controls .reset-btn,
.zoom-controls .dialog-close-btn {
    padding: 8px 12px;
    font-size: 16px;
    font-weight: bold;
    background-color: #4a2a2a;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.dialog-close-btn {
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.zoom-controls button:hover,
.zoom-controls .reset-btn:hover,
.zoom-controls .dialog-close-btn:hover {
    background-color: #6b3c3c;
    filter: drop-shadow(0 0 5px rgba(255, 100, 100, 0.5));
}

.zoom-controls button.selected,
.zoom-controls .reset-btn.selected {
    background-color: #90ee90;
    color: #222;
    filter: drop-shadow(0 0 5px rgba(144, 238, 144, 0.5));
}

.building-container {
    flex-grow: 1;
    position: relative;
    overflow: hidden;
    border-radius: 12px;
}

.scaled-svg {
    width: 100%;
    height: 100%;
    border: none;
}

@media (max-width: 768px) {
    .building-card {
        width: 95vw;
        height: 85vh;
    }

    .card-header h2 {
        font-size: 20px;
    }

    .info-card {
        width: 120px;
        padding: 6px 10px;
        top: 35px;
    }

    .card-title {
        font-size: 12px;
    }

    .close-btn {
        font-size: 16px;
    }

    .zoom-controls button,
    .zoom-controls .reset-btn,
    .zoom-controls .dialog-close-btn {
        padding: 6px 10px;
        font-size: 14px;
    }

    .dialog-close-btn svg {
        width: 20px;
        height: 20px;
    }
}

@media (max-width: 480px) {
    .card-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .right-section {
        margin-top: 10px;
    }

    .card-header h2 {
        font-size: 18px;
    }

    .info-card {
        width: 100px;
        padding: 5px 8px;
        top: 30px;
    }

    .card-title {
        font-size: 10px;
    }

    .close-btn {
        font-size: 14px;
    }

    .zoom-controls button,
    .zoom-controls .reset-btn,
    .zoom-controls .dialog-close-btn {
        padding: 5px 8px;
        font-size: 12px;
    }

    .dialog-close-btn svg {
        width: 18px;
        height: 18px;
    }
}
</style>